/**
 * ホテル配車（貸出場所指定）— 予約 json_data からの読み出し
 *
 * LP（new-n-car）が予約作成時に json_data.data へ日本語キーで立てる。
 * キー契約の正: booking-platform/md/hotel-delivery.md §3
 *   ホテル配車 / 配車区分 / 配車場所 / 配車返却先 / 配車返却先詳細 / 配車場所住所 / 配車場所地図 / 配車場所座標
 * 希望なしの予約にはキー自体が無い（'到着連絡: 有り' と同じ流儀）。
 */

export const HOTEL_DELIVERY_KEYS = {
  flag: 'ホテル配車',
  mode: '配車区分',
  place: '配車場所',
  returnPlace: '配車返却先',
  returnOther: '配車返却先詳細',
  address: '配車場所住所',
  mapUrl: '配車場所地図',
  latLng: '配車場所座標',
} as const;

export const HOTEL_DELIVERY_MODE_RETURN_ONLY = '返却のみ指定';
export const HOTEL_DELIVERY_MODE_DELIVERY = '貸出場所まで配車';

export interface HotelDelivery {
  /** 配車区分（'貸出場所まで配車' | '返却のみ指定'。旧データや欠損は '貸出場所まで配車' 扱い） */
  mode: string;
  /** 返却のみ指定か */
  returnOnly: boolean;
  /** 貸出場所（返却のみ指定のときは返却先）の名称 */
  place: string;
  /** 返却場所（'ホテル' | '店舗' | 'その他'）。返却のみ指定のとき、または未設定（旧予約など）は null */
  returnPlace: string | null;
  /** 返却場所が 'その他' のときの場所名 */
  returnOther: string;
  /** Google Places で選ばれていた場合の住所 / 地図URL / 座標 */
  address: string;
  mapUrl: string;
  latLng: string;
}

/** json_data（文字列 / {data,types} / 旧配列形式）から data オブジェクトを取り出す */
export function extractJsonDataRecord(jsonData: unknown): Record<string, unknown> | null {
  let jd: unknown = jsonData;
  if (typeof jd === 'string') {
    try { jd = JSON.parse(jd); } catch { return null; }
  }
  if (Array.isArray(jd)) jd = jd[0]; // 2026-09-08 以前の LP は配列で送っていた
  if (!jd || typeof jd !== 'object') return null;
  const data = (jd as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
}

const str = (v: unknown): string => (v == null ? '' : String(v).trim());

/**
 * ホテル配車情報を返す。希望なし（キー無し）は null。
 */
export function parseHotelDelivery(jsonData: unknown): HotelDelivery | null {
  const data = extractJsonDataRecord(jsonData);
  if (!data) return null;
  const flag = str(data[HOTEL_DELIVERY_KEYS.flag]);
  if (!flag) return null;
  const modeRaw = str(data[HOTEL_DELIVERY_KEYS.mode]);
  const returnOnly = modeRaw === HOTEL_DELIVERY_MODE_RETURN_ONLY;
  const mode = returnOnly ? HOTEL_DELIVERY_MODE_RETURN_ONLY : HOTEL_DELIVERY_MODE_DELIVERY;
  const returnPlaceRaw = str(data[HOTEL_DELIVERY_KEYS.returnPlace]);
  return {
    mode,
    returnOnly,
    place: str(data[HOTEL_DELIVERY_KEYS.place]),
    returnPlace: returnOnly ? null : (returnPlaceRaw || null),
    returnOther: returnOnly ? '' : str(data[HOTEL_DELIVERY_KEYS.returnOther]),
    address: str(data[HOTEL_DELIVERY_KEYS.address]),
    mapUrl: str(data[HOTEL_DELIVERY_KEYS.mapUrl]),
    latLng: str(data[HOTEL_DELIVERY_KEYS.latLng]),
  };
}

/** 一覧やツールチップ向けの1行要約。例: "配車: テストホテルA → 返却: ホテル" / "返却のみ: Yホテル" */
export function formatHotelDeliverySummary(h: HotelDelivery): string {
  const place = h.place || '未入力';
  if (h.returnOnly) return `返却のみ: ${place}`;
  let ret = h.returnPlace || '未設定';
  if (ret === 'その他') ret += `（${h.returnOther || '未入力'}）`;
  return `配車: ${place} → 返却: ${ret}`;
}

/** 地図リンク。Places の URL が無ければ座標 / 住所 / 名称から Google マップ検索 URL を作る */
export function hotelDeliveryMapUrl(h: HotelDelivery): string | null {
  if (h.mapUrl) return h.mapUrl;
  const q = h.latLng || h.address || h.place;
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

// ---------------------------------------------------------------------------
// 書き込み（サイドパネルからスタッフが変更する）
// ---------------------------------------------------------------------------

export const HOTEL_DELIVERY_RETURN_OPTIONS = ['ホテル', '店舗', 'その他'] as const;

export interface HotelDeliveryInput {
  returnOnly: boolean;
  place: string;
  address: string;
  /** 配車のときの返却場所。'' = 未設定 */
  returnPlace: string;
  returnOther: string;
}

/** 既存の HotelDelivery → 編集フォームの初期値 */
export function toHotelDeliveryInput(h: HotelDelivery | null): HotelDeliveryInput {
  return {
    returnOnly: !!h?.returnOnly,
    place: h?.place || '',
    address: h?.address || '',
    returnPlace: h?.returnPlace || '',
    returnOther: h?.returnOther || '',
  };
}

/**
 * json_data にホテル配車の値を書き込んだ新しい json_data を返す（元は変更しない）。
 * - input=null: ホテル配車なし → 配車系キーをすべて削除
 * - 形式は {data,types} に正規化（文字列 / 旧配列形式は変換）。他のキーは維持
 * - 場所名または住所が変わったら Places 由来の地図URL/座標は捨てる（食い違いを残さない）
 */
export function applyHotelDelivery(
  jsonData: unknown,
  input: HotelDeliveryInput | null,
): { data: Record<string, unknown>; types: Record<string, unknown> } {
  let jd: unknown = jsonData;
  if (typeof jd === 'string') {
    try { jd = JSON.parse(jd); } catch { jd = null; }
  }
  if (Array.isArray(jd)) jd = jd[0];
  const base = (jd && typeof jd === 'object') ? (jd as Record<string, unknown>) : {};
  const data: Record<string, unknown> = { ...((base.data as Record<string, unknown>) || {}) };
  const types: Record<string, unknown> = { ...((base.types as Record<string, unknown>) || {}) };
  const K = HOTEL_DELIVERY_KEYS;
  const before = parseHotelDelivery({ data });

  const del = (k: string) => { delete data[k]; delete types[k]; };
  const put = (k: string, v: string) => { data[k] = v; types[k] = 'text'; };

  if (!input) {
    Object.values(K).forEach(del);
    return { ...base, data, types };
  }
  const place = input.place.trim();
  const address = input.address.trim();
  put(K.flag, '希望');
  put(K.mode, input.returnOnly ? HOTEL_DELIVERY_MODE_RETURN_ONLY : HOTEL_DELIVERY_MODE_DELIVERY);
  put(K.place, place || '未入力');
  if (address) put(K.address, address); else del(K.address);
  if (input.returnOnly) {
    del(K.returnPlace); del(K.returnOther);
  } else {
    const rp = (HOTEL_DELIVERY_RETURN_OPTIONS as readonly string[]).includes(input.returnPlace) ? input.returnPlace : '';
    if (rp) put(K.returnPlace, rp); else del(K.returnPlace);
    if (rp === 'その他') put(K.returnOther, input.returnOther.trim() || '未入力'); else del(K.returnOther);
  }
  if (before && (before.place !== place || before.address !== address)) {
    del(K.mapUrl); del(K.latLng);
  }
  return { ...base, data, types };
}
